import { initializeApp, getApps, getApp } from "firebase/app";
import { connectFirestoreEmulator, initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from "firebase/firestore";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

// Solo firebase/app + firebase/firestore acá a propósito: este archivo lo importa
// use-content.ts, que se usa en TODAS las páginas públicas (Inicio, Método, Clases, etc.)
// para leer el contenido editable. firebase/auth, firebase/storage y firebase/functions
// pesan bastante y solo hacen falta en /admin, /staff y /portal (que ya bajan aparte por
// code-splitting) — así que viven en client-auth.ts, client-storage.ts y
// client-functions.ts, para que un visitante que solo mira el sitio público no las descargue.

const apiKey = import.meta.env.VITE_FIREBASE_API_KEY as string;
const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string;
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as string;
const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string;
const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string;
const appId = import.meta.env.VITE_FIREBASE_APP_ID as string;

export const isFirebaseConfigured = Boolean(apiKey && authDomain && projectId && appId);

if (!isFirebaseConfigured) {
  // El sitio sigue funcionando con los contenidos por defecto
  // (src/lib/content-defaults.ts), pero /admin y /staff no van a poder
  // iniciar sesión ni guardar cambios. Ver firebase/SETUP.md.
  console.warn(
    "[CP] Firebase no está configurado (faltan VITE_FIREBASE_*). " +
    "Los paneles /admin y /staff no van a poder iniciar sesión. Ver firebase/SETUP.md"
  );
}

const firebaseConfig = {
  apiKey: apiKey || "placeholder-key",
  authDomain: authDomain || "placeholder.firebaseapp.com",
  projectId: projectId || "placeholder",
  storageBucket: storageBucket || "placeholder.firebasestorage.app",
  messagingSenderId: messagingSenderId || "0",
  appId: appId || "placeholder",
};

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
export const firebaseApp = app;

// App Check (ver auditoría de seguridad): agrega una capa de "¿este pedido viene de verdad de
// nuestro sitio, corriendo en un navegador real?" delante de Firestore/Storage/Functions —
// mitiga scripts que le pegan directo a la API sin pasar por la app (ej. inflar analytics/
// ctaClicks, floodear contactLeads). Se inicializa ACÁ (no en un archivo aparte) para que
// cubra TODO uso de Firebase, incluidas las páginas públicas (home, /contacto) que solo
// importan este archivo — son justo las que más lo necesitan.
//
// VITE_RECAPTCHA_SITE_KEY es el Key ID de una clave de reCAPTCHA Enterprise tipo "score-based"
// (no la clave clásica de reCAPTCHA v3 — Firebase la marca obsoleta en favor de Enterprise, y
// como App Check todavía no estaba activado en producción cuando se detectó esto, se migró de
// una vez en vez de dejar deuda técnica). No es un secreto (no es la "secret key", esa nunca va
// en el cliente). Sin esta variable la app sigue funcionando normal (App Check simplemente no
// se activa) — así un checkout local sin la variable configurada no se rompe.
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;
if (recaptchaSiteKey) {
  // En desarrollo (localhost), activa el token de depuración de App Check en vez de intentar
  // resolver un reCAPTCHA real contra un dominio no registrado — sin esto, trabajar en local
  // contra un proyecto con App Check "Enforce" activado queda bloqueado de entrada. El token se
  // imprime en la consola del navegador la primera vez; hay que registrarlo a mano en Firebase
  // Console → App Check → tu app web → "Manage debug tokens" para que se acepte.
  if (import.meta.env.DEV) {
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
} else if (import.meta.env.PROD) {
  console.warn(
    "[CP] VITE_RECAPTCHA_SITE_KEY no está configurada — App Check no se activó. " +
    "Si ya activaste 'Enforce' en Firebase Console para Firestore/Storage/Functions, TODA " +
    "escritura/lectura real va a empezar a fallar hasta que se configure esta variable."
  );
}

// Caché local persistente (IndexedDB) en vez de getFirestore() a secas: sin esto, cualquier
// escritura hecha con mala señal (típico en el gimnasio) simplemente fallaba en el momento —
// ej. marcar una serie completada durante Entrenar — en vez de quedar en cola y reintentarse
// sola cuando vuelve la conexión. persistentMultipleTabManager (en vez del single-tab por
// defecto) evita que la app quede inutilizable con un error "failed-precondition" si alguien
// del staff tiene CP Admin abierto en más de una pestaña.
export const firestore = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

// Solo para pruebas de punta a punta contra los emuladores (scripts/e2e): se activa con VITE_USE_EMULATOR=1 al compilar.
// Sin esa variable (producción y desarrollo normal) no hace nada.
if (import.meta.env.VITE_USE_EMULATOR === "1") connectFirestoreEmulator(firestore, "127.0.0.1", 8089);
